import { Component, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";

import { apiDelete, apiGet, apiPost, apiPut } from "./consignmentsApi";
import CardLookupDialog from "../sessions/CardLookupDialog";

const STATUS_OPTIONS = [
  "DRAFT",
  "PROPOSED",
  "ACCEPTED",
  "REJECTED",
  "RECEIVED",
  "READY_TO_LIST",
  "LISTING_CREATED",
];

function pickDefaultSearchType(searchTypes) {
  const list = searchTypes || [];
  if (!list.length) return "";
  const raw = list.find((st) => String(st || "").toUpperCase() === "RAW");
  return String(raw || list[0]);
}

function StatusChip({ value }) {
  const label = value || "";
  return <Chip size="small" label={label} variant="outlined" />;
}


class PanelErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorMessage: "" };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, errorMessage: String(error?.message || error) };
  }
  componentDidCatch(error, info) {
    // Keep console visibility for debugging but prevent full-page blank.
    // eslint-disable-next-line no-console
    console.error("PanelErrorBoundary caught error:", error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <Box sx={{ p: 2, border: "1px solid rgba(0,0,0,0.12)", borderRadius: 1 }}>
          <Typography sx={{ fontWeight: 800, mb: 1 }}>
            {this.props.title || "Panel error"}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            {this.state.errorMessage || "An unexpected error occurred rendering this panel."}
          </Typography>
        </Box>
      );
    }
    return this.props.children;
  }
}


export default function ConsignmentsDashboard() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [consignors, setConsignors] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [selectedSessionId, setSelectedSessionId] = useState("");
  const [sessionDetail, setSessionDetail] = useState(null);
  const [searchTypes, setSearchTypes] = useState([]);

  const [tab, setTab] = useState(0);

  // Inline edit buffers (avoid saving on every keystroke)
  const [editBuffers, setEditBuffers] = useState({});

  const [savingFields, setSavingFields] = useState({});

  const [bulkReceiveDate, setBulkReceiveDate] = useState("");
  const [bulkRunning, setBulkRunning] = useState(false);

  // Create consignor
  const [newConsignorName, setNewConsignorName] = useState("");
  const [newConsignorContact, setNewConsignorContact] = useState("");
  const [newConsignorCommType, setNewConsignorCommType] = useState("PERCENT");
  const [newConsignorCommValue, setNewConsignorCommValue] = useState(10);

  // Create session
  const [newSessionName, setNewSessionName] = useState("");
  const [newSessionConsignorId, setNewSessionConsignorId] = useState("");

  // Add card
  const [newCardName, setNewCardName] = useState("");
  const [newCardSearchType, setNewCardSearchType] = useState("");
  const [newCardCommType, setNewCardCommType] = useState("");
  const [newCardCommValue, setNewCardCommValue] = useState("");
  const [newCardFloor, setNewCardFloor] = useState("");

  // Add card modal (manual + find)
  const [addCardModalOpen, setAddCardModalOpen] = useState(false);
  const [lookupOpen, setLookupOpen] = useState(false);

  const applyAddCardDefaults = () => {
    // Search type defaults to RAW when available.
    const st = pickDefaultSearchType(searchTypes);
    if (st) setNewCardSearchType(st);

    // Commission defaults to consignor defaults for this session (if available).
    const t = selectedSession?.default_commission_type;
    const v = selectedSession?.default_commission_value;

    setNewCardCommType(t ? String(t) : "");
    setNewCardCommValue(v !== null && v !== undefined && v !== "" ? String(v) : "");
  };

  const openAddCardModal = () => {
    applyAddCardDefaults();
    setAddCardModalOpen(true);
  };;

  // Finance (ledger + payout runs)
  const [financeConsignorId, setFinanceConsignorId] = useState("");
  const [financeLoading, setFinanceLoading] = useState(false);
  const [financeError, setFinanceError] = useState(null);
  const [consignorBalance, setConsignorBalance] = useState(null);
  const [ledgerEntries, setLedgerEntries] = useState([]);
  const [payoutRuns, setPayoutRuns] = useState([]);
  const [auditEvents, setAuditEvents] = useState([]);
  const [consignorCardsPayouts, setConsignorCardsPayouts] = useState([]);
  const [payoutPeriodStart, setPayoutPeriodStart] = useState("");
  const [payoutPeriodEnd, setPayoutPeriodEnd] = useState("");

  // Extra safety: never allow render-time crashes if state shape is unexpected.
  const safeLedgerEntries = Array.isArray(ledgerEntries) ? ledgerEntries : [];
  const safePayoutRuns = Array.isArray(payoutRuns) ? payoutRuns : [];
  const safeConsignorCardsPayouts = Array.isArray(consignorCardsPayouts) ? consignorCardsPayouts : [];
  const safeAuditEvents = Array.isArray(auditEvents) ? auditEvents : [];

  const refreshAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const [c, s, st] = await Promise.all([
        apiGet("/api/consignors"),
        apiGet("/api/consignment-sessions"),
        apiGet("/api/search-types"),
      ]);
      setConsignors(c || []);
      setSessions(s || []);
      setSearchTypes(st || []);
      if (!newSessionConsignorId && (c || []).length) {
        setNewSessionConsignorId(String(c[0].id));
      }
      if (!newCardSearchType && (st || []).length) {
        setNewCardSearchType(pickDefaultSearchType(st));
      }
    } catch (e) {
      setError(String(e?.message || e));
    } finally {
      setLoading(false);
    }
  };

  const refreshSession = async (sessionId) => {
    if (!sessionId) return;
    setLoading(true);
    setError(null);
    try {
      const detail = await apiGet(`/api/consignment-sessions/${sessionId}`);
      setSessionDetail(detail);
    } catch (e) {
      setError(String(e?.message || e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedSessionId) refreshSession(selectedSessionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSessionId]);

  useEffect(() => {
    if (tab === 5 && financeConsignorId) {
      refreshFinance(financeConsignorId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, financeConsignorId]);

  const selectedSession = sessionDetail?.session;
  const sessionCards = sessionDetail?.cards || [];

  const sessionSearchSessionId = selectedSession?.search_session_id;

  useEffect(() => {
    if (selectedSession?.consignor_id) {
      setFinanceConsignorId(String(selectedSession.consignor_id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSession?.consignor_id]);

  const defaultCommission = useMemo(() => {
    return {
      type: selectedSession?.default_commission_type || "PERCENT",
      value:
        selectedSession?.default_commission_value !== undefined
          ? selectedSession.default_commission_value
          : 10,
    };
  }, [selectedSession]);

  const createConsignor = async () => {
    setLoading(true);
    setError(null);
    try {
      await apiPost("/api/consignors", {
        name: newConsignorName,
        contact: newConsignorContact,
        default_commission_type: newConsignorCommType,
        default_commission_value: Number(newConsignorCommValue || 0),
      });
      setNewConsignorName("");
      setNewConsignorContact("");
      await refreshAll();
    } catch (e) {
      setError(String(e?.message || e));
    } finally {
      setLoading(false);
    }
  };

  const createSession = async () => {
    setLoading(true);
    setError(null);
    try {
      const created = await apiPost("/api/consignment-sessions", {
        consignor_id: Number(newSessionConsignorId),
        session_name: newSessionName,
      });
      await refreshAll();
      setNewSessionName("");
      if (created?.id) {
        setSelectedSessionId(String(created.id));
        setTab(1);
      }
    } catch (e) {
      setError(String(e?.message || e));
    } finally {
      setLoading(false);
    }
  };

  const addCard = async () => {
    if (!selectedSessionId) return;
    setLoading(true);
    setError(null);
    try {
      await apiPost(`/api/consignment-sessions/${selectedSessionId}/cards`, {
        card_name: newCardName,
        search_type: newCardSearchType,
        commission_type: newCardCommType || null,
        commission_value:
          newCardCommValue === "" || newCardCommValue === null
            ? null
            : Number(newCardCommValue),
        floor_price: newCardFloor === "" ? null : Number(newCardFloor),
      });
      setNewCardName("");
      setNewCardCommType("");
      setNewCardCommValue("");
      setNewCardFloor("");
      await refreshSession(selectedSessionId);
    } catch (e) {
      setError(String(e?.message || e));
    } finally {
      setLoading(false);
    }
  };

  const addCardWithName = async (cardName) => {
    if (!selectedSessionId) return;
    const name = String(cardName || "").trim();
    if (!name) return;
    setLoading(true);
    setError(null);
    try {
      await apiPost(`/api/consignment-sessions/${selectedSessionId}/cards`, {
        card_name: name,
        search_type: newCardSearchType,
        commission_type: newCardCommType || null,
        commission_value:
          newCardCommValue === "" || newCardCommValue === null
            ? null
            : Number(newCardCommValue),
        floor_price: newCardFloor === "" ? null : Number(newCardFloor),
      });
      setNewCardName("");
      await refreshSession(selectedSessionId);
      setAddCardModalOpen(false);
    } catch (e) {
      setError(String(e?.message || e));
    } finally {
      setLoading(false);
    }
  };

  
  const updateCard = async (cardId, patch, opts = {}) => {
    const { refresh = true, silent = false } = opts || {};
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const updated = await apiPut(`/api/consignment-cards/${cardId}`, patch);
      if (refresh) {
        await refreshSession(selectedSessionId);
      } else {
        // Minimal: patch local session state to avoid focus/typing interruptions.
        setSessionDetail((prev) => {
          if (!prev || !Array.isArray(prev.cards)) return prev;
          return {
            ...prev,
            cards: prev.cards.map((c) => (c.id === cardId ? { ...c, ...updated, ...patch } : c)),
          };
        });
      }
      return updated;
    } catch (e) {
      if (!silent) setError(String(e?.message || e));
      throw e;
    } finally {
      if (!silent) setLoading(false);
    }
  };


  const setEditBuffer = (cardId, field, value) => {
    setEditBuffers((prev) => ({
      ...prev,
      [cardId]: {
        ...(prev[cardId] || {}),
        [field]: value,
      },
    }));
  };


  const toDatetimeLocal = (v) => {
    if (!v) return "";
    return String(v).replace(" ", "T").slice(0, 16);
  };

  const nowSql = () => new Date().toISOString().slice(0, 19).replace("T", " ");

  const defaultReceivedQty = (row) => {
    const current = row?.received_qty;
    if (current !== null && current !== undefined && String(current).trim() !== "" && Number(current) > 0) return current;
    const expected = row?.expected_qty;
    if (expected !== null && expected !== undefined && String(expected).trim() !== "" && Number(expected) > 0) return expected;
    return 1;
  };

  const runBulkCardUpdates = async (cards, patchForRow) => {
    if (!selectedSessionId) return;
    if (!Array.isArray(cards) || cards.length === 0) return;

    setBulkRunning(true);
    setError(null);

    try {
      for (const row of cards) {
        const patch = patchForRow(row);
        if (!patch) continue;
        await updateCard(row.id, patch, { refresh: false, silent: true });
      }
      await refreshSession(selectedSessionId);
    } catch (e) {
      setError(String(e?.message || e));
    } finally {
      setBulkRunning(false);
    }
  };

  const acceptAllCards = async () => {
    await runBulkCardUpdates(sessionCards, () => ({ status: "ACCEPTED" }));
  };

  const receiveAllCardsWithDate = async () => {
    if (!bulkReceiveDate) {
      setError("Please pick a received date first.");
      return;
    }
    const receivedAt = `${bulkReceiveDate} 00:00:00`;
    await runBulkCardUpdates(sessionCards, (row) => ({
      status: "RECEIVED",
      received_at: receivedAt,
      received_qty: defaultReceivedQty(row),
    }));
  };

  const markAllCardsReceivedNow = async () => {
    await runBulkCardUpdates(sessionCards, (row) => ({
      status: "RECEIVED",
      received_at: nowSql(),
      received_qty: defaultReceivedQty(row),
    }));
  };

  const commitNumberField = async (cardId, field, rawValue) => {
    const v = rawValue;

    // Track per-field saving to show small UI hints and avoid double submits.
    setSavingFields((prev) => ({
      ...prev,
      [cardId]: { ...(prev[cardId] || {}), [field]: true },
    }));

    try {
      if (v === "" || v === null || v === undefined) {
        // commission_value must be sent with commission_type to remain backwards compatible with the API contract
        if (field === "commission_value") {
          const current = sessionCards.find((c) => c.id === cardId);
          const ctype = current?.commission_type || "";
          await updateCard(cardId, { commission_type: ctype || null, commission_value: null }, { refresh: false, silent: true });
        } else {
          await updateCard(cardId, { [field]: null }, { refresh: false, silent: true });
        }
        return;
      }

      const num = Number(v);
      if (Number.isNaN(num)) {
        setError(`Invalid number for ${field}`);
        return;
      }

      if (field === "commission_value") {
        const current = sessionCards.find((c) => c.id === cardId);
        const ctype = current?.commission_type || "";
        await updateCard(cardId, { commission_type: ctype || null, commission_value: num }, { refresh: false, silent: true });
      } else {
        await updateCard(cardId, { [field]: num }, { refresh: false, silent: true });
      }
    } finally {
      setSavingFields((prev) => ({
        ...prev,
        [cardId]: { ...(prev[cardId] || {}), [field]: false },
      }));
    }
  };


  const commitIntField = async (cardId, field, rawValue) => {
    const v = rawValue;
    setSavingFields((prev) => ({ ...prev, [cardId]: { ...(prev[cardId] || {}), [field]: true } }));
    try {
      if (v === "" || v === null || v === undefined) {
        await updateCard(cardId, { [field]: null }, { refresh: false, silent: true });
        return;
      }
      const num = parseInt(v, 10);
      if (Number.isNaN(num)) {
        setError(`Invalid integer for ${field}`);
        return;
      }
      await updateCard(cardId, { [field]: num }, { refresh: false, silent: true });
    } finally {
      setSavingFields((prev) => ({ ...prev, [cardId]: { ...(prev[cardId] || {}), [field]: false } }));
    }
  };

  const commitDatetimeField = async (cardId, field, rawValue) => {
    const v = rawValue;
    setSavingFields((prev) => ({ ...prev, [cardId]: { ...(prev[cardId] || {}), [field]: true } }));
    try {
      if (v === "" || v === null || v === undefined) {
        await updateCard(cardId, { [field]: null }, { refresh: false, silent: true });
        return;
      }
      // Expect HTML datetime-local value like "2026-02-15T09:35"
      const asSql = String(v).replace("T", " ").slice(0, 19);
      await updateCard(cardId, { [field]: asSql }, { refresh: false, silent: true });
    } finally {
      setSavingFields((prev) => ({ ...prev, [cardId]: { ...(prev[cardId] || {}), [field]: false } }));
    }
  };

  const commitTextField = async (cardId, field, rawValue) => {
    const v = rawValue;
    setSavingFields((prev) => ({ ...prev, [cardId]: { ...(prev[cardId] || {}), [field]: true } }));
    try {
      await updateCard(cardId, { [field]: v === "" ? null : v }, { refresh: false, silent: true });
    } finally {
      setSavingFields((prev) => ({ ...prev, [cardId]: { ...(prev[cardId] || {}), [field]: false } }));
    }
  };
  const deleteCard = async (cardId) => {
    setLoading(true);
    setError(null);
    try {
      await apiDelete(`/api/consignment-cards/${cardId}`);
      await refreshSession(selectedSessionId);
    } catch (e) {
      setError(String(e?.message || e));
    } finally {
      setLoading(false);
    }
  };

  const triggerScrape = async () => {
    if (!sessionSearchSessionId) return;
    setLoading(true);
    setError(null);
    try {
      await apiPost("/api/run-scraper", { search_session_id: sessionSearchSessionId });
      // UI note: scrape is async; user can refresh.
    } catch (e) {
      setError(String(e?.message || e));
    } finally {
      setLoading(false);
    }
  };

  const generateListings = async () => {
    if (!selectedSessionId) return;
    setLoading(true);
    setError(null);
    try {
      await apiPost(`/api/consignment-sessions/${selectedSessionId}/generate-listings`, {});
      await refreshSession(selectedSessionId);
    } catch (e) {
      setError(String(e?.message || e));
    } finally {
      setLoading(false);
    }
  };


  const refreshFinance = async (consignorId) => {
    const cid = consignorId || financeConsignorId;
    if (!cid) return;
    setFinanceLoading(true);
    setFinanceError(null);
    try {
      const [bal, led, runs, cards, aud] = await Promise.all([
        apiGet(`/api/consignors/${cid}/balance`),
        apiGet(`/api/consignors/${cid}/ledger`),
        apiGet(`/api/consignors/${cid}/payout-runs`),
        apiGet(`/api/consignors/${cid}/cards-payouts`),
        apiGet(`/api/audit/events?entity_type=consignor&entity_id=${cid}&limit=50`),
      ]);
      setConsignorBalance(bal || null);
      // Defensive parsing: backend may return {entries: []} or [] or an error object.
      const ledArr = (led && Array.isArray(led.entries)) ? led.entries : (Array.isArray(led) ? led : []);
      const runsArr = (runs && Array.isArray(runs.runs)) ? runs.runs : (Array.isArray(runs) ? runs : []);
      setLedgerEntries(ledArr);
      setPayoutRuns(runsArr);
      const cardsArr = Array.isArray(cards) ? cards : (cards && Array.isArray(cards.cards)) ? cards.cards : [];
      setConsignorCardsPayouts(cardsArr);
    } catch (e) {
      setFinanceError(String(e?.message || e));
    } finally {
      setFinanceLoading(false);
    }
  };

  const createPayoutRun = async () => {
    if (!financeConsignorId) return;
    setFinanceLoading(true);
    setFinanceError(null);
    try {
      await apiPost(`/api/consignors/${financeConsignorId}/payout-runs`, {
        period_start: payoutPeriodStart ? new Date(payoutPeriodStart).toISOString() : null,
        period_end: payoutPeriodEnd ? new Date(payoutPeriodEnd).toISOString() : null,
      });
      await refreshFinance(financeConsignorId);
    } catch (e) {
      setFinanceError(String(e?.message || e));
    } finally {
      setFinanceLoading(false);
    }
  };

  const updatePayoutRunStatus = async (runId, status, paymentReference) => {
    setFinanceLoading(true);
    setFinanceError(null);
    try {
      await apiPut(`/api/payout-runs/${runId}/status`, {
        status,
        payment_reference: paymentReference || null,
      });
      await refreshFinance(financeConsignorId);
    } catch (e) {
      setFinanceError(String(e?.message || e));
    } finally {
      setFinanceLoading(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 800 }}>
          Consignments (MVP)
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button variant="outlined" onClick={refreshAll} disabled={loading}>
            Refresh
          </Button>
        </Stack>
      </Stack>

      {error ? (
        <Card sx={{ mb: 2, border: "1px solid rgba(239,68,68,0.35)" }}>
          <CardContent>
            <Typography sx={{ color: "rgba(239,68,68,1)", fontWeight: 700 }}>
              {error}
            </Typography>
          </CardContent>
        </Card>
      ) : null}

      <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              Consignors
            </Typography>
            <Stack spacing={1} sx={{ mb: 2 }}>
              <TextField
                size="small"
                label="Name"
                value={newConsignorName}
                onChange={(e) => setNewConsignorName(e.target.value)}
              />
              <TextField
                size="small"
                label="Contact"
                value={newConsignorContact}
                onChange={(e) => setNewConsignorContact(e.target.value)}
              />
              <Stack direction="row" spacing={1}>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>Commission</InputLabel>
                  <Select
                    label="Commission"
                    value={newConsignorCommType}
                    onChange={(e) => setNewConsignorCommType(e.target.value)}
                  >
                    <MenuItem value="PERCENT">Percent</MenuItem>
                    <MenuItem value="FLAT">Flat</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  label={newConsignorCommType === "FLAT" ? "Flat $" : "%"}
                  value={newConsignorCommValue}
                  onChange={(e) => setNewConsignorCommValue(e.target.value)}
                />
                <Button
                  variant="contained"
                  onClick={createConsignor}
                  disabled={loading || !newConsignorName.trim()}
                >
                  Add
                </Button>
              </Stack>
            </Stack>

            <Divider sx={{ mb: 2 }} />
            <Stack spacing={1}>
              {(consignors || []).map((c) => (
                <Card key={c.id} variant="outlined">
                  <CardContent sx={{ py: 1.25, "&:last-child": { pb: 1.25 } }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography sx={{ fontWeight: 800 }}>{c.name}</Typography>
                        <Typography variant="body2" sx={{ opacity: 0.8 }}>
                          {c.contact || ""}
                        </Typography>
                      </Box>
                      <Chip
                        size="small"
                        label={`${c.default_commission_type || "PERCENT"} ${c.default_commission_value}`}
                      />
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </CardContent>
        </Card>

        <Card sx={{ flex: 2 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              Consignment Sessions
            </Typography>

            <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ mb: 2 }}>
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel>Consignor</InputLabel>
                <Select
                  label="Consignor"
                  value={newSessionConsignorId}
                  onChange={(e) => setNewSessionConsignorId(e.target.value)}
                >
                  {(consignors || []).map((c) => (
                    <MenuItem key={c.id} value={String(c.id)}>
                      {c.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size="small"
                label="Session name"
                value={newSessionName}
                onChange={(e) => setNewSessionName(e.target.value)}
                fullWidth
              />
              <Button
                variant="contained"
                onClick={createSession}
                disabled={loading || !newSessionConsignorId}
              >
                New Session
              </Button>
            </Stack>

            <FormControl size="small" fullWidth sx={{ mb: 2 }}>
              <InputLabel>Open session</InputLabel>
              <Select
                label="Open session"
                value={selectedSessionId}
                onChange={(e) => setSelectedSessionId(e.target.value)}
              >
                {(sessions || []).map((s) => (
                  <MenuItem key={s.id} value={String(s.id)}>
                    #{s.id} — {s.session_name} ({s.consignor_name || ""})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {!selectedSession ? (
              <Typography sx={{ opacity: 0.7 }}>Select a session to manage cards.</Typography>
            ) : (
              <>
                <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <Typography sx={{ fontWeight: 800 }}>
                    Session #{selectedSession.id} — {selectedSession.session_name}
                  </Typography>
                  <Chip size="small" label={`Search Session: ${sessionSearchSessionId}`} />
                  <Chip
                    size="small"
                    label={`Default: ${defaultCommission.type} ${defaultCommission.value}`}
                    variant="outlined"
                  />
                </Stack>

                <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 1 }}>
                  <Tab label="Cards" />
                  <Tab label="Scrape" />
                  <Tab label="Acceptance" />
                  <Tab label="Receiving" />
                  <Tab label="Generate Listings" />
                  <Tab label="Finance" />
                </Tabs>
                <Divider sx={{ mb: 2 }} />

                {tab === 0 ? (
                  <>
                    <Typography sx={{ fontWeight: 700, mb: 1 }}>Add card</Typography>
                    <Stack direction="row" spacing={1} sx={{ mb: 2, alignItems: "center" }}>
                      <Button
                        variant="contained"
                        onClick={() => {
                                                    openAddCardModal();
                        }}
                        disabled={!selectedSessionId}
                      >
                        Add cards…
                      </Button>
                      <Typography variant="body2" sx={{ opacity: 0.75 }}>
                        Manual entry or find/search cards (same flow as Sessions).
                      </Typography>
                    </Stack>

                    <Dialog
  open={addCardModalOpen}
  onClose={() => setAddCardModalOpen(false)}
  fullWidth
  maxWidth="lg"
  PaperProps={{
    sx: {
      minHeight: "420px",
      width: "900px",
      maxWidth: "95vw"
    }
  }}
>
  <DialogTitle>Add cards</DialogTitle>
  <DialogContent>
    <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ mb: 1 }}>
      <TextField
        size="small"
        label="Card name"
        value={newCardName}
        onChange={(e) => setNewCardName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") addCard();
        }}
        fullWidth
      />
      <FormControl size="small" sx={{ minWidth: 220 }}>
        <InputLabel>Search type</InputLabel>
        <Select
          label="Search type"
          value={newCardSearchType}
          onChange={(e) => setNewCardSearchType(e.target.value)}
        >
          {(searchTypes || []).map((st) => (
            <MenuItem key={st} value={String(st)}>
              {st}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ minWidth: 180 }}>
        <InputLabel>Commission type</InputLabel>
        <Select
          label="Commission type"
          value={newCardCommType}
          onChange={(e) => setNewCardCommType(e.target.value)}
        >
          <MenuItem value="">Default</MenuItem>
          <MenuItem value="PERCENT">Percent</MenuItem>
          <MenuItem value="FLAT">Flat</MenuItem>
        </Select>
      </FormControl>
      <TextField
        size="small"
        label="Commission value"
        type="number"
        value={newCardCommValue}
        onChange={(e) => setNewCardCommValue(e.target.value)}
        sx={{ width: 160 }}
      />
      <TextField
        size="small"
        label="Floor"
        type="number"
        value={newCardFloor}
        onChange={(e) => setNewCardFloor(e.target.value)}
        sx={{ width: 120 }}
      />
    </Stack>

    <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
      <Button
        variant="contained"
        onClick={addCard}
        disabled={loading || !newCardName.trim() || !newCardSearchType}
      >
        Add Card
      </Button>
      <Button
        variant="outlined"
        onClick={() => { applyAddCardDefaults(); setLookupOpen(true); }}
        disabled={!newCardSearchType}
      >
        Find Card
      </Button>
      <Typography variant="body2" sx={{ opacity: 0.75, alignSelf: "center" }}>
        Find Card uses the same lookup modal as Sessions.
      </Typography>
    </Stack>
  </DialogContent>

  <DialogActions>
    <Button onClick={() => setAddCardModalOpen(false)}>Close</Button>
  </DialogActions>
</Dialog>

<CardLookupDialog
  open={lookupOpen}
  onClose={() => setLookupOpen(false)}
  onAdd={async (card) => {
    await addCardWithName(card?.name_and_number || "");
  }}
  onPick={(card) => {
    setNewCardName(card?.name_and_number || "");
  }}
/>
>

                    <Typography sx={{ fontWeight: 700, mb: 1 }}>Session cards</Typography>
                    <Stack spacing={1}>
                      {sessionCards.map((row) => (
                        <Card key={row.id} variant="outlined">
                          <CardContent sx={{ py: 1.25, "&:last-child": { pb: 1.25 } }}>
                            <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ md: "center" }} justifyContent="space-between">
                              <Box sx={{ flex: 1 }}>
                                <Typography sx={{ fontWeight: 800 }}>
                                  {row.card_name}
                                </Typography>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5, flexWrap: "wrap" }}>
                                  <StatusChip value={row.status} />
                                  <Chip size="small" label={`Scrape: ${row.scrape_status || ""}`} variant="outlined" />
                                  {row.predicted_sale_price != null ? (
                                    <Chip size="small" label={`Pred: $${Number(row.predicted_sale_price).toFixed(2)}`} />
                                  ) : row.avg_price_30 != null ? (
                                    <Chip size="small" label={`Avg30: $${Number(row.avg_price_30).toFixed(2)}`} />
                                  ) : null}
                                </Stack>
                              </Box>

                              <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ md: "center" }}>
                                <FormControl size="small" sx={{ minWidth: 150 }}>
                                  <InputLabel>Status</InputLabel>
                                  <Select
                                    label="Status"
                                    value={row.status || "DRAFT"}
                                    onChange={(e) => updateCard(row.id, { status: e.target.value })}
                                  >
                                    {STATUS_OPTIONS.map((s) => (
                                      <MenuItem key={s} value={s}>
                                        {s}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                                <Button color="error" variant="outlined" onClick={() => deleteCard(row.id)}>
                                  Delete
                                </Button>
                              </Stack>
                            </Stack>
                          </CardContent>
                        </Card>
                      ))}
                    </Stack>
                  </>
                ) : null}

                {tab === 1 ? (
                  <>
                    <Typography sx={{ mb: 1 }}>
                      Run a scrape using the linked <b>Search Session</b>. This uses your existing batch orchestrator.
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <Button variant="contained" onClick={triggerScrape} disabled={loading || !sessionSearchSessionId}>
                        Scrape Session
                      </Button>
                      <Button variant="outlined" onClick={() => refreshSession(selectedSessionId)} disabled={loading}>
                        Refresh Results
                      </Button>
                    </Stack>
                    <Typography variant="body2" sx={{ mt: 2, opacity: 0.75 }}>
                      Tip: scrape runs in background. Refresh after a few minutes.
                    </Typography>
                  </>
                ) : null}

                {tab === 2 ? (
                  <>
                    <Typography sx={{ mb: 1 }}>
                      Acceptance: set commission terms + mark cards ACCEPTED/REJECTED.
                    </Typography>
                    <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ mb: 1 }} alignItems={{ md: "center" }}>
                      <Button
                        variant="contained"
                        onClick={acceptAllCards}
                        disabled={loading || bulkRunning || sessionCards.length === 0}
                      >
                        Accept All Cards
                      </Button>
                      {bulkRunning ? <CircularProgress size={18} /> : null}
                      <Typography variant="body2" sx={{ opacity: 0.75 }}>
                        Bulk updates run card-by-card then refresh once.
                      </Typography>
                    </Stack>

                    <Stack spacing={1}>
                      {sessionCards.map((row) => (
                        <Card key={row.id} variant="outlined">
                          <CardContent sx={{ py: 1.25, "&:last-child": { pb: 1.25 } }}>
                            <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ md: "center" }} justifyContent="space-between">
                              <Box sx={{ flex: 1 }}>
                                <Typography sx={{ fontWeight: 800 }}>{row.card_name}</Typography>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5, flexWrap: "wrap" }}>
                                  <StatusChip value={row.status} />
                                  {row.predicted_sale_price != null ? (
                                    <Chip size="small" label={`Pred: $${Number(row.predicted_sale_price).toFixed(2)}`} />
                                  ) : row.avg_price_30 != null ? (
                                    <Chip size="small" label={`Avg30: $${Number(row.avg_price_30).toFixed(2)}`} />
                                  ) : (
                                    <Chip size="small" label="No summary yet" variant="outlined" />
                                  )}
                                </Stack>
                              </Box>

                              <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ md: "center" }}>
                                <FormControl size="small" sx={{ minWidth: 140 }}>
                                  <InputLabel>Comm</InputLabel>
                                  <Select
                                    label="Comm"
                                    value={row.commission_type || ""}
                                    onChange={(e) => updateCard(row.id, { commission_type: e.target.value || null })}
                                  >
                                    <MenuItem value="">(Default)</MenuItem>
                                    <MenuItem value="PERCENT">Percent</MenuItem>
                                    <MenuItem value="FLAT">Flat</MenuItem>
                                  </Select>
                                </FormControl>
                                <TextField
                                  size="small"
                                  label="Value"
                                  disabled={!!savingFields[row.id]?.commission_value}
                                  value={
                                    (editBuffers[row.id] && editBuffers[row.id].commission_value !== undefined)
                                      ? editBuffers[row.id].commission_value
                                      : (row.commission_value ?? "")
                                  }
                                  onChange={(e) => setEditBuffer(row.id, "commission_value", e.target.value)}
                                  onBlur={async (e) => {
                                    const v = e.target.value;
                                    // clear buffer after commit to reflect saved value on refresh
                                    setEditBuffer(row.id, "commission_value", undefined);
                                    await commitNumberField(row.id, "commission_value", v);
                                  }}
                                  helperText={savingFields[row.id]?.commission_value ? "Saving..." : ""}
                                  sx={{ width: 120 }}
                                />
                                <TextField
                                  size="small"
                                  label="Floor $"
                                  value={
                                    (editBuffers[row.id] && editBuffers[row.id].floor_price !== undefined)
                                      ? editBuffers[row.id].floor_price
                                      : (row.floor_price ?? "")
                                  }
                                  onChange={(e) => setEditBuffer(row.id, "floor_price", e.target.value)}
                                  onBlur={async (e) => {
                                    const v = e.target.value;
                                    setEditBuffer(row.id, "floor_price", undefined);
                                    await commitNumberField(row.id, "floor_price", v);
                                  }}
                                  sx={{ width: 120 }}
                                />
                                <FormControl size="small" sx={{ minWidth: 150 }}>
                                  <InputLabel>Status</InputLabel>
                                  <Select
                                    label="Status"
                                    value={row.status || "DRAFT"}
                                    onChange={(e) => updateCard(row.id, { status: e.target.value })}
                                  >
                                    <MenuItem value="PROPOSED">PROPOSED</MenuItem>
                                    <MenuItem value="ACCEPTED">ACCEPTED</MenuItem>
                                    <MenuItem value="REJECTED">REJECTED</MenuItem>
                                  </Select>
                                </FormControl>
                              </Stack>
                            </Stack>
                          </CardContent>
                        </Card>
                      ))}
                    </Stack>
                  </>
                ) : null}

                {tab === 3 ? (
                  <>
                    <Typography sx={{ mb: 1 }}>
                      Receiving: mark what physically arrived.
                    </Typography>
                    <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ mb: 1 }} alignItems={{ md: "center" }}>
                      <TextField
                        size="small"
                        label="Received date"
                        type="date"
                        value={bulkReceiveDate}
                        onChange={(e) => setBulkReceiveDate(e.target.value)}
                        sx={{ width: 190 }}
                        InputLabelProps={{ shrink: true }}
                      />
                      <Button
                        variant="contained"
                        onClick={receiveAllCardsWithDate}
                        disabled={loading || bulkRunning || sessionCards.length === 0 || !bulkReceiveDate}
                      >
                        Receive All (Pick Date)
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={markAllCardsReceivedNow}
                        disabled={loading || bulkRunning || sessionCards.length === 0}
                      >
                        Mark All Received (Now)
                      </Button>
                      {bulkRunning ? <CircularProgress size={18} /> : null}
                    </Stack>

                    <Stack spacing={1}>
                      {sessionCards.map((row) => (
                        <Card key={row.id} variant="outlined">
                          <CardContent sx={{ py: 1.25, "&:last-child": { pb: 1.25 } }}>
                            <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ md: "center" }} justifyContent="space-between">
                              <Box sx={{ flex: 1 }}>
                                <Typography sx={{ fontWeight: 800 }}>{row.card_name}</Typography>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5, flexWrap: "wrap" }}>
                                  <StatusChip value={row.status} />
                                  {row.listing_id ? (
                                    <Chip size="small" label={`Listing: ${row.listing_id}`} />
                                  ) : null}
                                </Stack>
                              </Box>
                              <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ md: "center" }}>
                                <FormControl size="small" sx={{ minWidth: 170 }}>
                                  <InputLabel>Status</InputLabel>
                                  <Select
                                    label="Status"
                                    value={row.status || "DRAFT"}
                                    onChange={(e) =>
                                      updateCard(row.id, {
                                        status: e.target.value,
                                        received_at:
                                          e.target.value === "RECEIVED" ||
                                          e.target.value === "READY_TO_LIST"
                                            ? new Date().toISOString().slice(0, 19).replace("T", " ")
                                            : null,
                                      })
                                    }
                                  >
                                    <MenuItem value="ACCEPTED">ACCEPTED</MenuItem>
                                    <MenuItem value="RECEIVED">RECEIVED</MenuItem>
                                    <MenuItem value="READY_TO_LIST">READY_TO_LIST</MenuItem>
                                  </Select>
                                </FormControl>
                                <TextField
                                  size="small"
                                  label="Expected"
                                  value={
                                    (editBuffers[row.id] && editBuffers[row.id].expected_qty !== undefined)
                                      ? editBuffers[row.id].expected_qty
                                      : (row.expected_qty ?? "")
                                  }
                                  onChange={(e) => setEditBuffer(row.id, "expected_qty", e.target.value)}
                                  onBlur={async (e) => {
                                    const v = e.target.value;
                                    setEditBuffer(row.id, "expected_qty", undefined);
                                    await commitIntField(row.id, "expected_qty", v);
                                  }}
                                  helperText={savingFields[row.id]?.expected_qty ? "Saving..." : ""}
                                  sx={{ width: 110 }}
                                />
                                <TextField
                                  size="small"
                                  label="Received"
                                  value={
                                    (editBuffers[row.id] && editBuffers[row.id].received_qty !== undefined)
                                      ? editBuffers[row.id].received_qty
                                      : (row.received_qty ?? "")
                                  }
                                  onChange={(e) => setEditBuffer(row.id, "received_qty", e.target.value)}
                                  onBlur={async (e) => {
                                    const v = e.target.value;
                                    setEditBuffer(row.id, "received_qty", undefined);
                                    await commitIntField(row.id, "received_qty", v);
                                  }}
                                  helperText={savingFields[row.id]?.received_qty ? "Saving..." : ""}
                                  sx={{ width: 110 }}
                                />
                                <TextField
                                  size="small"
                                  label="Received at"
                                  type="datetime-local"
                                  value={
                                    (editBuffers[row.id] && editBuffers[row.id].received_at !== undefined)
                                      ? editBuffers[row.id].received_at
                                      : toDatetimeLocal(row.received_at)
                                  }
                                  onChange={(e) => setEditBuffer(row.id, "received_at", e.target.value)}
                                  onBlur={async (e) => {
                                    const v = e.target.value;
                                    setEditBuffer(row.id, "received_at", undefined);
                                    await commitDatetimeField(row.id, "received_at", v);
                                  }}
                                  helperText={savingFields[row.id]?.received_at ? "Saving..." : ""}
                                  sx={{ width: 200 }}
                                  InputLabelProps={{ shrink: true }}
                                />
                                <Button
                                  variant="outlined"
                                  onClick={() => updateCard(row.id, {
                                    status: "RECEIVED",
                                    received_at: nowSql(),
                                    received_qty: row.received_qty && Number(row.received_qty) > 0 ? row.received_qty : (row.expected_qty ?? 1),
                                  }, { refresh: true })}
                                >
                                  Mark Received
                                </Button>
                                <Button
                                  variant="outlined"
                                  onClick={() => updateCard(row.id, { status: "READY_TO_LIST" }, { refresh: true })}
                                >
                                  Ready to List
                                </Button>
                                <TextField
                                  size="small"
                                  label="Notes"
                                  value={
                                    (editBuffers[row.id] && editBuffers[row.id].notes !== undefined)
                                      ? editBuffers[row.id].notes
                                      : (row.notes ?? "")
                                  }
                                  onChange={(e) => setEditBuffer(row.id, "notes", e.target.value)}
                                  onBlur={async (e) => {
                                    const v = e.target.value;
                                    setEditBuffer(row.id, "notes", undefined);
                                    await commitTextField(row.id, "notes", v);
                                  }}
                                  helperText={savingFields[row.id]?.notes ? "Saving..." : ""}
                                  sx={{ width: 260 }}
                                />
                              </Stack>
                            </Stack>
                          </CardContent>
                        </Card>
                      ))}
                    </Stack>
                  </>
                ) : null}

                {tab === 4 ? (
                  <>
                    <Typography sx={{ mb: 1 }}>
                      Generate local listings for cards in status <b>RECEIVED</b> or <b>READY_TO_LIST</b>.
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <Button variant="contained" onClick={generateListings} disabled={loading}>
                        Generate Listings
                      </Button>
                      <Button variant="outlined" onClick={() => refreshSession(selectedSessionId)} disabled={loading}>
                        Refresh
                      </Button>
                    </Stack>
                    <Typography variant="body2" sx={{ mt: 2, opacity: 0.75 }}>
                      After generation, listings appear in your normal Listings module.
                    </Typography>
                  </>
                ) : null}

                {tab === 5 ? (
                  <PanelErrorBoundary title="Finance">
                    <>
                    <Typography sx={{ mb: 1 }}>
                      Finance: view ledger balance and manage payout runs for the selected consignor.
                    </Typography>

                    <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ mb: 2 }} alignItems={{ md: "center" }}>
                      <FormControl size="small" sx={{ minWidth: 260 }}>
                        <InputLabel>Consignor</InputLabel>
                        <Select
                          label="Consignor"
                          value={financeConsignorId || ""}
                          onChange={(e) => setFinanceConsignorId(String(e.target.value))}
                        >
                          {(consignors || []).map((c) => (
                            <MenuItem key={c.id} value={String(c.id)}>
                              {c.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <Button variant="outlined" onClick={() => refreshFinance(financeConsignorId)} disabled={financeLoading}>
                        Refresh
                      </Button>

                      <Chip
                        label={`Balance: $${Number(consignorBalance?.balance || 0).toFixed(2)}`}
                        variant="outlined"
                      />
                      {financeError ? <Chip color="error" label={financeError} /> : null}
                    </Stack>

                    <Card variant="outlined" sx={{ mb: 2 }}>
                      <CardContent>
                        <Typography sx={{ fontWeight: 800, mb: 1 }}>Cards & payouts</Typography>
                        <Typography variant="body2" sx={{ opacity: 0.8, mb: 1 }}>
                          Per-card view of status, sale price, fees, net payout, and whether it has been paid.
                        </Typography>

                        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 520 }}>
                          <Table stickyHeader size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Card</TableCell>
                                <TableCell>Session</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right">Sale $</TableCell>
                                <TableCell align="right">Fees $</TableCell>
                                <TableCell align="right">Payout $</TableCell>
                                <TableCell>Payout Run</TableCell>
                                <TableCell>Paid</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {safeConsignorCardsPayouts.length ? (
                                safeConsignorCardsPayouts.map((r) => {
                                  const saleGross = Number(r.sale_gross || 0);
                                  const feesTotal = Math.abs(Number(r.ebay_fees || 0)) + Math.abs(Number(r.shipping_cost || 0)) + Math.abs(Number(r.ad_fees || 0)) + Math.abs(Number(r.commission_fee || 0)) + Math.abs(Number(r.adjustments || 0) < 0 ? Number(r.adjustments || 0) : 0);
                                  const payout = Number(r.net_payout || 0);
                                  const paid = (String(r.payout_run_status || "").toUpperCase() === "PAID");
                                  return (
                                    <TableRow key={r.consignment_session_card_id}>
                                      <TableCell>
                                        <Typography sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                                          {r.card_name || `Card #${r.consignment_session_card_id}`}
                                        </Typography>
                                        <Typography variant="caption" sx={{ opacity: 0.75 }}>
                                          {r.sku ? `SKU: ${r.sku}` : ""}
                                        </Typography>
                                      </TableCell>
                                      <TableCell>{r.session_name || ""}</TableCell>
                                      <TableCell><StatusChip value={r.card_status || ""} /></TableCell>
                                      <TableCell align="right">${saleGross.toFixed(2)}</TableCell>
                                      <TableCell align="right">${feesTotal.toFixed(2)}</TableCell>
                                      <TableCell align="right">${payout.toFixed(2)}</TableCell>
                                      <TableCell>
                                        {r.payout_run_id ? (
                                          <Chip size="small" label={`#${r.payout_run_id} ${r.payout_run_status || ""}`} variant="outlined" />
                                        ) : (
                                          <Chip size="small" label="Not assigned" variant="outlined" />
                                        )}
                                      </TableCell>
                                      <TableCell>
                                        {paid ? <Chip size="small" color="success" label="Paid" /> : <Chip size="small" label="Unpaid" variant="outlined" />}
                                      </TableCell>
                                    </TableRow>
                                  );
                                })
                              ) : (
                                <TableRow>
                                  <TableCell colSpan={8}>
                                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                                      No cards found for this consignor.
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </CardContent>
                    </Card>

                    <Card variant="outlined" sx={{ mb: 2 }}>
                      <CardContent>
                        <Typography sx={{ fontWeight: 800, mb: 1 }}>Create payout run</Typography>
                        <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ md: "center" }}>
                          <TextField
                            size="small"
                            label="Period start"
                            type="datetime-local"
                            value={payoutPeriodStart}
                            onChange={(e) => setPayoutPeriodStart(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ width: 240 }}
                          />
                          <TextField
                            size="small"
                            label="Period end"
                            type="datetime-local"
                            value={payoutPeriodEnd}
                            onChange={(e) => setPayoutPeriodEnd(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ width: 240 }}
                          />
                          <Button variant="contained" onClick={createPayoutRun} disabled={financeLoading || !financeConsignorId}>
                            Create
                          </Button>
                        </Stack>
                      </CardContent>
                    </Card>

                    <Card variant="outlined" sx={{ mb: 2 }}>
                      <CardContent>
                        <Typography sx={{ fontWeight: 800, mb: 1 }}>Payout runs</Typography>
                        <Stack spacing={1}>
                          {safePayoutRuns.length ? (
                            safePayoutRuns.map((r) => (
                              <Stack
                                key={r.id}
                                direction={{ xs: "column", md: "row" }}
                                spacing={1}
                                alignItems={{ md: "center" }}
                                justifyContent="space-between"
                              >
                                <Box sx={{ flex: 1 }}>
                                  <Typography sx={{ fontWeight: 700 }}>
                                    Run #{r.id} — {r.status}
                                  </Typography>
                                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                                    {r.period_start ? String(r.period_start) : ""} ? {r.period_end ? String(r.period_end) : ""}
                                  </Typography>
                                </Box>

                                <Chip label={`Payout: $${Number(r.payout_amount || 0).toFixed(2)}`} variant="outlined" />
                                <Chip label={`Balance: $${Number(r.total_balance || 0).toFixed(2)}`} variant="outlined" />

                                <TextField
                                  size="small"
                                  label="Payment ref"
                                  defaultValue={r.payment_reference || ""}
                                  onBlur={(e) => updatePayoutRunStatus(r.id, r.status, e.target.value)}
                                  sx={{ width: 220 }}
                                />

                                <FormControl size="small" sx={{ minWidth: 160 }}>
                                  <InputLabel>Status</InputLabel>
                                  <Select
                                    label="Status"
                                    value={r.status || "DRAFT"}
                                    onChange={(e) => updatePayoutRunStatus(r.id, e.target.value, r.payment_reference)}
                                  >
                                    <MenuItem value="DRAFT">DRAFT</MenuItem>
                                    <MenuItem value="APPROVED">APPROVED</MenuItem>
                                    <MenuItem value="PAID">PAID</MenuItem>
                                    <MenuItem value="CANCELLED">CANCELLED</MenuItem>
                                  </Select>
                                </FormControl>
                              </Stack>
                            ))
                          ) : (
                            <Typography variant="body2" sx={{ opacity: 0.7 }}>
                              No payout runs yet.
                            </Typography>
                          )}
                        </Stack>
                      </CardContent>
                    </Card>

                    <Card variant="outlined">
                      <CardContent>
                        <Typography sx={{ fontWeight: 800, mb: 1 }}>Ledger (latest 500)</Typography>
                        <Stack spacing={0.5}>
                          {safeLedgerEntries.length ? (
                            safeLedgerEntries.map((e) => (
                              <Stack
                                key={e.id}
                                direction={{ xs: "column", md: "row" }}
                                spacing={1}
                                alignItems={{ md: "center" }}
                                justifyContent="space-between"
                                sx={{ py: 0.5, borderBottom: "1px solid rgba(0,0,0,0.06)" }}
                              >
                                <Box sx={{ flex: 1 }}>
                                  <Typography sx={{ fontWeight: 700 }}>
                                    {e.entry_type} — ${Number(e.amount || 0).toFixed(2)}
                                  </Typography>
                                  <Typography variant="body2" sx={{ opacity: 0.75 }}>
                                    {e.occurred_at ? String(e.occurred_at) : ""}{e.listing_id ? ` • Listing ${e.listing_id}` : ""}
                                  </Typography>
                                </Box>
                              </Stack>
                            ))
                          ) : (
                            <Typography variant="body2" sx={{ opacity: 0.7 }}>
                              No ledger entries yet.
                            </Typography>
                          )}
                        </Stack>
                      </CardContent>
                    </Card>

              <Card sx={{ mt: 2 }}>
                <CardContent>
                  <Typography sx={{ fontWeight: 800, mb: 1 }}>Audit Events (latest 50)</Typography>
                  {safeAuditEvents.length ? (
                    <Box sx={{ maxHeight: 320, overflow: "auto" }}>
                      <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
                        <thead>
                          <tr>
                            <th style={{ textAlign: "left", padding: "6px 8px", borderBottom: "1px solid rgba(0,0,0,0.1)" }}>Date</th>
                            <th style={{ textAlign: "left", padding: "6px 8px", borderBottom: "1px solid rgba(0,0,0,0.1)" }}>Type</th>
                            <th style={{ textAlign: "left", padding: "6px 8px", borderBottom: "1px solid rgba(0,0,0,0.1)" }}>Severity</th>
                            <th style={{ textAlign: "left", padding: "6px 8px", borderBottom: "1px solid rgba(0,0,0,0.1)" }}>Summary</th>
                          </tr>
                        </thead>
                        <tbody>
                          {safeAuditEvents.map((evt) => (
                            <tr key={evt.id}>
                              <td style={{ padding: "6px 8px", borderBottom: "1px solid rgba(0,0,0,0.06)" }}>{evt.created_at}</td>
                              <td style={{ padding: "6px 8px", borderBottom: "1px solid rgba(0,0,0,0.06)" }}>{evt.event_type}</td>
                              <td style={{ padding: "6px 8px", borderBottom: "1px solid rgba(0,0,0,0.06)" }}>{evt.severity}</td>
                              <td style={{ padding: "6px 8px", borderBottom: "1px solid rgba(0,0,0,0.06)" }}>{evt.summary}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </Box>
                  ) : (
                    <Typography sx={{ color: "text.secondary" }}>No audit events yet.</Typography>
                  )}
                </CardContent>
              </Card>
                  </>
                                  </PanelErrorBoundary>
                ) : null}

              </>
            )}
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
}
